import "./InputText.scss";
import PropTypes from "prop-types";
import PopupLogo from "assets/icons/popup.svg";
import _ from "lodash";
import { FormattedMessage, useIntl } from "react-intl";
import { POPUP_STATUS, TAG_TYPES, VAR_TYPES } from "constants/constants";
import { useEffect, useState } from "react";
import InputTextComment from "./InputTextComment";
import InputTextComments from "./InputTextComments";
import ErrorPopupBtn from "components/alert-popup/ErrorPopupBtn";
import { ErrorPopup } from "components/alert-popup/ErrorPopup";
import CategoryTag from "core/tags/category-tag/CategoryTag";
import Icon from "core/icon/Icon";
import { COPY_EMAIL_SUCCESS } from "constants/popup-codes";

export const InputTextPopup = ({ left, topUp }) => {
  const { title, content } = topUp;
  const titleId = title?.id || "";
  const titleDefaultMessage = title?.defaultMessage || "";
  const contentId = content?.id || "";
  const contentDefaultMessage = content?.defaultMessage || "";

  return (
    <div
      className={`input-text-popup`}
      style={{ marginLeft: `${left / 10}px` }}
    >
      <div className="input-text-popup-title">
        <FormattedMessage id={titleId} defaultMessage={titleDefaultMessage} />
      </div>
      <div className="input-text-popup-content">
        <FormattedMessage
          id={contentId}
          defaultMessage={contentDefaultMessage}
        />
      </div>
    </div>
  );
};

InputTextPopup.propTypes = { left: PropTypes.number, topUp: PropTypes.object };

const InputText = ({
  afterFieldLabel,
  comment,
  commentSettings,
  comments,
  errors,
  id,
  isUnderline,
  label,
  marginLeft,
  maxLength,
  onChange,
  pattern,
  placeholder,
  readOnly,
  required,
  setComment,
  topUp,
  type,
  value,
  width,
  inputStyle,
  hasCategoryTag,
  lang,
  commentAction,
  customLabelFieldStyle,
  dataTestId,
  onClick,
  isCommentsInput,
  model,
  canUserCreateComment = true,
  canUserDeleteComment = true,
  setCode,
  setStatus,
  copyEmail,
  multilineReadOnly = false,
  ...props
}) => {
  const intl = useIntl();
  const [popupOpened, setPopupOpened] = useState(false);
  const [commentOpened, setCommentOpened] = useState(false);
  const [val, setVal] = useState(value || "");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isAddCommentAction, setIsAddCommentAction] = useState(false);
  const showCategoryTag = !!hasCategoryTag && !!val;

  useEffect(() => {
    setVal(value);
  }, [value]);

  const inputTextStyle = inputStyle
    ? {
        fontWeight: isFocused ? "normal" : inputStyle.fontWeight || "",
      }
    : {};

  const MAX_INPUT_WIDTH = 15;

  const renderInput = () => (
    <div style={{ position: "relative", width: "100%" }}>
      {((isCommentsInput && comments?.length > 0) || onClick) && (
        <button
          onClick={
            onClick ||
            (() => {
              handleCommentOnClick({
                type: isCommentsInput && `read-${id}-comments`,
              });
            })
          }
          style={{
            width: "140px",
            height: "25px",
            position: "absolute",
          }}
        ></button>
      )}
      {readOnly && multilineReadOnly ? (
        <div
          data-testid={dataTestId}
          className="input-placeholder input-placeholder--multiline sourcesanspro-normal-gray-suit-18px"
          id={`input-text-${id}`}
          style={inputTextStyle}
        >
          {val ?? ""}
        </div>
      ) : (
        <input
          data-testid={dataTestId}
          className="input-placeholder sourcesanspro-normal-gray-suit-18px"
          placeholder={
            typeof placeholder === VAR_TYPES.STRING
              ? placeholder
              : intl.formatMessage(placeholder)
          }
          name={props.name}
          onChange={(e) => {
            if (pattern) {
              if (pattern.test(e.target.value) || e.target.value === "") {
                setVal(e.target.value);
                onChange(e);
              }
            } else {
              setVal(e.target.value);
              onChange(e);
            }
          }}
          value={val ?? ""}
          type={type || "text"}
          disabled={readOnly}
          style={{
            ...inputTextStyle,
            textDecoration: isUnderline ? "underline" : "none",
            ...(afterFieldLabel
              ? {
                  width: `${Math.min(val?.length + 1, MAX_INPUT_WIDTH)}ch`,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }
              : {}),
          }}
          id={`input-text-${id}`}
          maxLength={maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      )}
    </div>
  );

  const renderButtonIcon = () => {
    if (_.size(comments) > 0 && !commentOpened && !isCommentsInput)
      return _.size(comments);
    else if (commentOpened) return "-";
    else if (!_.isEmpty(comment)) return "1";
    return "+";
  };

  const handleCommentOnClick = ({ type }) => {
    type && setIsAddCommentAction(type === `add-${id}-comment`);
    setCommentOpened((old) => !old);
  };

  return (
    <div
      className="input-text"
      style={{
        width: width ? `${width}px` : "inherit",
      }}
      id={`div-text-${id}`}
    >
      {canUserCreateComment &&
        ((commentSettings !== null &&
          (comment || comments?.length > 0 || !readOnly)) ||
          isCommentsInput) && (
          <button
            id={`input-text-commentSettings-${id}`}
            className="input-text-button-commentary"
            style={{
              marginLeft: `${marginLeft || 7.25}%`,
            }}
            onClick={(event) => {
              event.preventDefault();
              handleCommentOnClick({
                type: isCommentsInput && `add-${id}-comment`,
              });
            }}
          >
            {renderButtonIcon()}
          </button>
        )}
      <div
        className={`overlap-group ${label && "justify-input-text"}
        ${errors?.ref && "error"}

        `}
      >
        {label && (
          <div className="label sourcesanspro-normal-shadow-blue-14px error">
            <label
              className={`${
                errors?.ref ? "label-error" : "label-standard"
              } ${customLabelFieldStyle}`}
            >
              {typeof label === "string" ? (
                label
              ) : (
                <FormattedMessage {...label} />
              )}
              {required && "*"}
            </label>
            {topUp && (
              <span
                className="popup-information"
                onClick={() => setPopupOpened((old) => !old)}
              >
                <img className="popup-img" src={PopupLogo} alt="logo" />
              </span>
            )}
            {!readOnly &&
              (errors?.ref || errors?.type || props.errorMessage) && (
                <ErrorPopupBtn setShowErrorPopup={setShowErrorPopup} />
              )}
            {showErrorPopup && (
              <ErrorPopup
                errors={errors}
                showErrorPopup={showErrorPopup}
                setShowErrorPopup={setShowErrorPopup}
                label={label || label?.id}
                errorMessage={props.errorMessage}
                errorValue={props.errorValue}
              />
            )}
          </div>
        )}
        {showCategoryTag ? (
          <div tyle={{ margin: "4px 0 10px 0", maxWidth: "100%" }} title={val}>
            <CategoryTag
              key={`${id}-tag`}
              type={TAG_TYPES.PEOPLE}
              label={val}
              deleteOption={false}
              showing
            />
          </div>
        ) : afterFieldLabel ? (
          <div className="input-with-unit-container">
            {renderInput()}
            <span className="input-unit">{afterFieldLabel}</span>
          </div>
        ) : (
          renderInput()
        )}
      </div>
      {popupOpened && <InputTextPopup topUp={topUp} left={width} />}
      {commentOpened && !_.isArray(comments) && (
        <InputTextComment
          settings={commentSettings}
          value={comment}
          getValue={setComment}
          left={width}
          onClick={setCommentOpened}
          readOnly={readOnly}
          id={id}
          setCommentOpened={setCommentOpened}
          commentOpened={commentOpened}
          isCommentsInput={isCommentsInput}
          isAddCommentAction={isAddCommentAction}
        />
      )}
      {commentOpened && _.isArray(comments) > 0 && (
        <InputTextComments
          settings={commentSettings}
          value={comments}
          setComment={setComment}
          left={width}
          onClick={(val) => {
            props.addComment(val);
            isCommentsInput && setIsAddCommentAction(false);
            setCommentOpened(false);
          }}
          readOnly={readOnly}
          id={`input-text-comments-${id}`}
          setCommentOpened={setCommentOpened}
          isCommentsInput={isCommentsInput}
          isAddCommentAction={isAddCommentAction}
          lang={lang}
          commentAction={commentAction}
          canUserDeleteComment={canUserDeleteComment}
          model={model}
        />
      )}
      {copyEmail && (
        <Icon
          icon="copy"
          color="violet-clair"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setStatus(POPUP_STATUS.SUCCESS);
              setCode(COPY_EMAIL_SUCCESS);
            } catch (e) {
              console.error("Erreur", e);
            }
          }}
        />
      )}
    </div>
  );
};
InputText.propTypes = {
  stats: PropTypes.bool,
  addComment: PropTypes.func,
  comment: PropTypes.string,
  commentSettings: PropTypes.object,
  comments: PropTypes.array,
  id: PropTypes.string,
  isUnderline: PropTypes.bool,
  label: PropTypes.any,
  maxLength: PropTypes.number,
  onChange: PropTypes.func,
  pattern: PropTypes.any,
  placeholder: PropTypes.any,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  setComment: PropTypes.func,
  topUp: PropTypes.object,
  type: PropTypes.string,
  value: PropTypes.any,
  width: PropTypes.number,
  errors: PropTypes.object,
  marginLeft: PropTypes.number,
  errorMessage: PropTypes.any,
  errorValue: PropTypes.any,
  name: PropTypes.string,
  inputStyle: PropTypes.object,
  lang: PropTypes.string,
  commentAction: PropTypes.func,
  multilineReadOnly: PropTypes.bool,
};

InputText.defaultProps = {
  addComment: null,
  comment: null,
  commentSettings: null,
  comments: null,
  id: null,
  isUnderline: false,
  label: null,
  maxLength: null,
  onChange: null,
  pattern: null,
  placeholder: "",
  readOnly: false,
  required: false,
  setComment: null,
  topUp: null,
  multilineReadOnly: false,
  type: "text",
  value: null,
  width: null,
};

export default InputText;
